/**
 * Modal form for creating and editing FAQs
 */
import { Editor } from "@shared/components/Editor";
import { InputControl } from "@shared/components/form";
import { Button } from "@shared/components/ui/Button";
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { Faq, FaqFormData } from "../types/faqs.types";

interface FaqFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FaqFormData) => Promise<void>;
  faq?: Faq;
  isSubmitting?: boolean;
}

const FaqFormModal: React.FC<FaqFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  faq,
  isSubmitting = false,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryInput, setCategoryInput] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or faq changes
  useEffect(() => {
    if (isOpen) {
      if (faq) {
        setTitle(faq.title);
        setDescription(faq.description);
        setCategories(faq.categories);
      } else {
        setTitle("");
        setDescription("");
        setCategories([]);
      }
      setCategoryInput("");
      setErrors({});
    }
  }, [isOpen, faq]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    if (categories.length === 0) {
      newErrors.categories = "At least one category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        title,
        description,
        categories,
      });

      onClose();
      toast.success(faq ? "FAQ updated successfully" : "FAQ created successfully");
    } catch (error) {
      console.error("Error submitting FAQ:", error);
      toast.error("Failed to save FAQ. Please try again.");
    }
  };

  const addCategory = () => {
    const trimmedCategory = categoryInput.trim();

    if (!trimmedCategory) {
      return;
    }

    if (!categories.includes(trimmedCategory)) {
      setCategories([...categories, trimmedCategory]);
    }

    setCategoryInput("");
  };

  const removeCategory = (categoryToRemove: string) => {
    setCategories(categories.filter(category => category !== categoryToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCategory();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">
              {faq ? "Edit FAQ" : "Add New FAQ"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <InputControl
                label="Title"
                id="faq-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter FAQ title"
                error={errors.title}
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <Editor
                initialValue={description}
                onChange={setDescription}
                placeholder="Enter FAQ description"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">
                Categories
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a category"
                  className="form-input flex-grow mr-2"
                />
                <Button
                  type="button"
                  onClick={addCategory}
                  disabled={!categoryInput.trim()}
                >
                  Add
                </Button>
              </div>
              {errors.categories && (
                <p className="mt-1 text-sm text-red-500">{errors.categories}</p>
              )}

              {categories.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <div
                      key={category}
                      className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-md flex items-center text-sm"
                    >
                      <span>{category}</span>
                      <button
                        type="button"
                        onClick={() => removeCategory(category)}
                        className="ml-1 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                {faq ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FaqFormModal;
